'use client'
import Header from '@/app/components/Header'
import HotelListPlaceholder from '@/app/components/HotelsListPlaceholder'
import HotelsWithErrorBoundary from '@/app/components/HotelsWithErrorBoundary'
import { emitResultsPageDirectViewGAEvent } from '@/app/ga-event-helpers'
import useIsGAReady from '@/app/hooks/useIsGAReady'
import { getLocaleCodeByLang } from '@/app/languages'
import { DestPagesProps } from '@/app/types'
import { parseSearchParamsToLower } from '@/app/utils'
import { DIRECT_VIEW_GA_EVENT_MESSAGE } from '../../../../../utils/sentry-errors'
import { captureEvent, captureException } from '@sentry/nextjs'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { NextIntlClientProvider } from 'next-intl'
import { FC, Suspense, useEffect, useRef, useState } from 'react'

interface DestPageClientProps extends DestPagesProps {
	messages: any
}

const DestPageClient: FC<DestPageClientProps> = (props) => {
	const { messages, searchParams } = props
	const searchParamsId = Object.values(searchParams).join('')
	const lowerCaseParams = parseSearchParamsToLower(searchParams)
	const sessionID = lowerCaseParams?.['sessionid']
	const isGAReady = useIsGAReady()
	const mountTimeRef = useRef(Date.now())
	const eventFiredRef = useRef(false)

	const [queryClient] = useState(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						// With SSR, we usually want to set some default staleTime
						// above 0 to avoid refetching immediately on the client
						staleTime: 60 * 1000, // 1 minute
					},
				},
			})
	)

	useEffect(() => {
		const GA_EVENT_DELAY_MS = 1500

		if (!sessionID && isGAReady && !eventFiredRef.current) {
			const timeoutId = setTimeout(() => {
				try {
					emitResultsPageDirectViewGAEvent()
					eventFiredRef.current = true

					captureEvent({
						message: DIRECT_VIEW_GA_EVENT_MESSAGE,
						level: 'info',
						tags: {
							event_type: 'direct_view_diagnostic',
							component: 'DestPageClient',
						},
						extra: {
							sessionID: sessionID || 'none',
							hasSessionID: !!sessionID,
							gaReadyDelay: Date.now() - mountTimeRef.current,
							url:
								typeof window !== 'undefined'
									? window.location.href
									: 'unknown',
							referrer:
								typeof document !== 'undefined' ? document.referrer : 'unknown',
							timestamp: new Date().toISOString(),
						},
					})
				} catch (error) {
					// Always capture errors
					captureException(error, {
						tags: {
							component: 'DestPageClient',
							action: 'emitResultsPageDirectViewGAEvent',
						},
						extra: {
							sessionID: sessionID || 'none',
							isGAReady,
							error: error instanceof Error ? error.message : String(error),
						},
					})
				}
			}, GA_EVENT_DELAY_MS)

			return () => clearTimeout(timeoutId)
		}
	}, [isGAReady, sessionID])

	const localeCode = getLocaleCodeByLang(props.params.lang)
	return (
		<main className="results-page">
			<NextIntlClientProvider
				locale={localeCode}
				messages={messages}
				timeZone={Intl.DateTimeFormat().resolvedOptions().timeZone}
			>
				<QueryClientProvider client={queryClient}>
					<Header params={props} />
					<Suspense
						key={
							props.searchParams.page +
							props.params.dest.join('') +
							searchParamsId
						}
						fallback={
							<>
								<div className="container">
									<Header params={props} loading />
								</div>
								<HotelListPlaceholder />
							</>
						}
					>
						<div className="min-h-[60vh] sm:pt-4 bg-text-200">
							<HotelsWithErrorBoundary {...props} />
						</div>
					</Suspense>
				</QueryClientProvider>
			</NextIntlClientProvider>
		</main>
	)
}

export default DestPageClient
